import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";

const OfflineContext = createContext(null);
export const useOffline = () => useContext(OfflineContext);

const MAX_QUEUE = 10;

export function OfflineProvider({ children }) {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showBackOnline, setShowBackOnline] = useState(false);
  const queueRef = useRef([]);

  // Enqueue a failed action for replay when online
  const enqueue = useCallback((action) => {
    const q = queueRef.current;
    if (q.length >= MAX_QUEUE) q.shift(); // drop oldest
    q.push(action);
  }, []);

  // Replay all queued actions
  const replay = useCallback(async () => {
    const q = [...queueRef.current];
    queueRef.current = [];
    for (const action of q) {
      try {
        await action();
      } catch {
        /* best effort */
      }
    }
  }, []);

  useEffect(() => {
    const goOffline = () => {
      setIsOffline(true);
      setShowBackOnline(false);
    };
    const goOnline = () => {
      setIsOffline(false);
      setShowBackOnline(true);
      replay();
      setTimeout(() => setShowBackOnline(false), 3000);
    };

    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, [replay]);

  return (
    <OfflineContext.Provider value={{ isOffline, showBackOnline, enqueue }}>
      {children}
    </OfflineContext.Provider>
  );
}
